// ./src/components/features/Travels/TravelWaypointsDrawer/WaypointEditModal.tsx
import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  Stack,
  NumberInput,
  Textarea,
  Text,
  Divider,
} from "@mantine/core";
import { DateTimePicker } from "@mantine/dates";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { OpResult } from "@sdflc/api-helpers";

import { Expense, ExpenseInput } from "@/utils/api";
import { useUserProfileContext, useAppEnvironment } from "@/contexts";
import { EXPENSE_TYPES } from "@/config";
import { localStringToUtc, ApiError, stringifyObject } from "@/utils";
import { usePlaceLocation, PlaceInitialValues } from "@/hooks/usePlaceLocation";
import { AddressData } from "@/hooks/useGoogleMapsAutocomplete";
import { AppDrawer } from "@/components/generic";
import { PlaceLocationSection } from "@/components/features";


dayjs.extend(utc);

// ============================================
// Types
// ============================================

export interface WaypointEditModalProps {
  opened: boolean;
  onClose: () => void;
  waypoint: Expense | null;
  onSave: (id: string, data: ExpenseInput) => Promise<OpResult | null>;
  loading?: boolean;
}

interface FormState {
  whenDone: string;
  pointType: string | null | undefined;
  odometer: number | null;
  comments: string;
}

// ============================================
// Helper Functions
// ============================================

const utcToLocalString = (utcDateString: string | null | undefined): string => {
  if (!utcDateString) return dayjs().format("YYYY-MM-DD HH:mm");
  try {
    return dayjs.utc(utcDateString).local().format("YYYY-MM-DD HH:mm");
  } catch {
    return dayjs().format("YYYY-MM-DD HH:mm");
  }
};

const getFormStateFromWaypoint = (waypoint: Expense | null): FormState => ({
  whenDone: utcToLocalString(waypoint?.whenDone),
  pointType: waypoint?.pointType || null,
  odometer: waypoint?.odometer ?? null,
  comments: waypoint?.comments || "",
});

/**
 * Build PlaceInitialValues from an existing waypoint Expense.
 */
const buildPlaceInitialValues = (
  waypoint: Expense | null
): PlaceInitialValues | undefined => {
  if (!waypoint) return undefined;

  return {
    whereDone: waypoint.whereDone || "",
    location: waypoint.location || "",
    latitude: waypoint.latitude ?? null,
    longitude: waypoint.longitude ?? null,
    savedPlaceId: waypoint.savedPlaceId ?? null,
    pointType: waypoint.pointType ?? null,
    addressData: waypoint.address1
      ? ({
        addressId: "",
        displayName: null,
        address1: waypoint.address1 || null,
        address2: waypoint.address2 || null,
        city: waypoint.city || null,
        country: waypoint.countryId || null,
        latitude: waypoint.latitude ?? null,
        longitude: waypoint.longitude ?? null,
        postalCode: waypoint.postalCode || null,
        region: waypoint.stateProvince || null,
      } as AddressData)
      : null,
  };
};

// ============================================
// Constants
// ============================================

const KNOWN_FORM_FIELDS = [
  "pointType",
  "odometer",
  "whereDone",
  "location",
  "latitude",
  "longitude",
  "comments",
  "whenDone",
  "savedPlaceId",
  "savePlace",
];

// ============================================
// Component
// ============================================

export const WaypointEditModal: React.FC<WaypointEditModalProps> = ({
  opened,
  onClose,
  waypoint,
  onSave,
  loading = false,
}) => {
  const { t } = useTranslation(["travels", "common"]);
  const { distanceLabelShort } = useUserProfileContext();
  const { isMobile } = useAppEnvironment();

  // Form state (non-location fields)
  const [formState, setFormState] = useState<FormState>(() =>
    getFormStateFromWaypoint(waypoint)
  );

  // Place/location state via reusable hook
  const placeLocation = usePlaceLocation({
    enabled: opened,
    autoLocate: false, // Editing existing waypoint — no GPS
    includePointType: true,
    onFieldChange: () => setPlaceDirty(true),
  });

  // Error state
  const [lastError, setLastError] = useState<ApiError | null>(null);

  // ── Dirty State Tracking ──
  const initialSnapshotRef = useRef<string>("");
  const [isDirty, setIsDirty] = useState(false);
  const [placeDirty, setPlaceDirty] = useState(false);

  useEffect(() => {
    if (!opened) return;
    const formChanged = stringifyObject(formState) !== initialSnapshotRef.current;
    setIsDirty(formChanged || placeDirty);
  }, [formState, placeDirty, opened]);

  // ── Error Handling ──
  const { fields: fieldErrors, genericErrors } = useMemo(() => {
    return ApiError.getFormErrors(lastError, KNOWN_FORM_FIELDS);
  }, [lastError]);

  const formattedGenericError = useMemo(() => {
    return ApiError.formatErrors(genericErrors);
  }, [genericErrors]);

  // ── Reset form when drawer opens with a waypoint ──
  useEffect(() => {
    if (opened && waypoint) {
      const newFormState = getFormStateFromWaypoint(waypoint);
      setFormState(newFormState);
      initialSnapshotRef.current = stringifyObject(newFormState);
      setIsDirty(false);
      setPlaceDirty(false);
      setLastError(null);

      placeLocation.reset(buildPlaceInitialValues(waypoint));
    }
  }, [opened, waypoint]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Handlers ──

  const handleWhenDoneChange = useCallback((value: string | null) => {
    setFormState((prev) => ({
      ...prev,
      whenDone: value || dayjs().format("YYYY-MM-DD HH:mm"),
    }));
  }, []);

  const handleOdometerChange = useCallback((value: string | number) => {
    setFormState((prev) => ({
      ...prev,
      odometer: typeof value === "number" ? value : null,
    }));
  }, []);

  const handleCommentsChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setFormState((prev) => ({ ...prev, comments: e.target.value }));
    },
    []
  );

  // ── Submit ──

  const handleSave = useCallback(async () => {
    if (!waypoint?.id) return;

    setLastError(null);

    const placeFields = placeLocation.getPlaceFields();

    const data: ExpenseInput = {
      expenseType: EXPENSE_TYPES.TRAVEL_POINT,
      whenDone: localStringToUtc(formState.whenDone),
      pointType: placeFields.pointType || undefined,
      odometer: formState.odometer ?? undefined,
      comments: formState.comments || undefined,
      whereDone: placeFields.whereDone || undefined,
      location: placeFields.location || undefined,
      address1: placeFields.address1 || undefined,
      address2: placeFields.address2 || undefined,
      city: placeFields.city || undefined,
      stateProvince: placeFields.stateProvince || undefined,
      postalCode: placeFields.postalCode || undefined,
      countryId: placeFields.countryId || undefined,
      latitude: placeFields.latitude ?? undefined,
      longitude: placeFields.longitude ?? undefined,
      savedPlaceId: placeFields.savedPlaceId || undefined,
      savePlace: placeFields.savePlace || undefined,
    };

    try {
      const result = await onSave(waypoint.id, data);

      if (result && !result.didSucceed()) {
        setLastError(ApiError.fromResult(result, "Failed to save waypoint"));
      } else {
        setIsDirty(false);
      }
    } catch (error) {
      setLastError(ApiError.fromException(error, "Failed to save waypoint"));
    }
  }, [waypoint?.id, formState, placeLocation, onSave]);

  // ============================================
  // Render
  // ============================================

  return (
    <AppDrawer
      opened={opened}
      onClose={onClose}
      title={t("travels:waypoints.editModal.title")}
      position="right"
      size="lg"
      responsive
      mobileHeightPercent={90}
      closable={!loading}
      loading={loading}
      errors={formattedGenericError}
      actionType="save-cancel"
      onConfirm={handleSave}
      isDirty={isDirty}
    >
      <Stack gap="md">
        {/* Date/Time */}
        <DateTimePicker
          label={t("travels:waypoints.editModal.fields.whenDone.label")}
          value={formState.whenDone}
          onChange={handleWhenDoneChange}
          maxDate={new Date()}
          size="md"
          valueFormat="MMM DD, YYYY HH:mm"
          dropdownType={isMobile ? "modal" : "popover"}
          timePickerProps={{
            withDropdown: true,
            popoverProps: { withinPortal: true },
            format: "12h",
          }}
        />

        <Divider
          label={t("travels:waypoints.editModal.sections.location")}
          labelPosition="center"
        />

        {/* Place, Address, Map Preview, Save Place Toggle */}
        <PlaceLocationSection
          placeLocation={placeLocation}
          showPointType
          fieldErrors={fieldErrors}
          placeNameLabel={t("travels:waypoints.editModal.fields.whereDone.label")}
          placeNamePlaceholder={t("travels:waypoints.editModal.fields.whereDone.placeholder")}
          addressLabel={t("travels:waypoints.editModal.fields.location.label")}
          addressPlaceholder={t("travels:waypoints.editModal.fields.location.placeholder")}
        />

        <Divider />

        {/* Odometer */}
        <NumberInput
          label={t("travels:waypoints.editModal.fields.odometer.label")}
          placeholder={t("travels:waypoints.editModal.fields.odometer.placeholder")}
          value={formState.odometer ?? ""}
          onChange={handleOdometerChange}
          error={fieldErrors.odometer}
          min={0}
          max={9999999}
          rightSection={
            <Text size="sm" c="dimmed" pr="sm">
              {t(`common:${distanceLabelShort}`)}
            </Text>
          }
          rightSectionWidth={50}
          size="md"
          hideControls
        />

        {/* Notes */}
        <Textarea
          label={t("travels:waypoints.editModal.fields.comments.label")}
          placeholder={t("travels:waypoints.editModal.fields.comments.placeholder")}
          value={formState.comments}
          onChange={handleCommentsChange}
          error={fieldErrors.comments}
          minRows={2}
          maxRows={4}
          autosize
          size="md"
        />
      </Stack>
    </AppDrawer>
  );
};